import type { SxProps, Theme } from '@mui/material/styles';

import Tooltip from '@mui/material/Tooltip';
import IconButton from '@mui/material/IconButton';

import { useTranslate } from 'src/locales';
import Iconify from 'src/components/iconify';
import { LinkType } from 'src/services/common/common.types';
import { LinkTypeMeta } from 'src/services/common.types';

import { normalizeExternalUrl } from './linkedin-url';

/**
 * LinkedIn brand blue. Deliberately not a theme token: `info.main` is cyan (#00B8D9) and `primary`
 * is the tenant's own colour, so neither reads as LinkedIn. A fixed brand value also stays correct
 * in dark mode, where a theme-derived grey washes out.
 */
const LINKEDIN_BLUE = '#0A66C2';

export type LinkedInLinkProps = {
  /** Raw stored URL. Detail headers resolve it with `linkedinUrlFromLinks`; lean lists send a scalar. */
  url?: string | null;
  size?: number;
  sx?: SxProps<Theme>;
};

/**
 * Small outbound LinkedIn glyph rendered beside a person's name (JT1-1480).
 *
 * Renders nothing at all when there is no usable URL — no placeholder and no reserved space, so a
 * person without LinkedIn causes no layout shift. In list rows the name itself is a button that
 * opens the record panel, so the click must not propagate.
 */
export default function LinkedInLink({ url, size = 16, sx }: LinkedInLinkProps) {
  const { t } = useTranslate();

  const href = normalizeExternalUrl(url);
  if (!href) return null;

  return (
    <Tooltip title={t('common.view_linkedin', { defaultValue: 'View LinkedIn profile' })}>
      <IconButton
        size="small"
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={t('common.view_linkedin', { defaultValue: 'View LinkedIn profile' })}
        onClick={(event) => event.stopPropagation()}
        sx={{ color: LINKEDIN_BLUE, p: 0.25, ...sx }}
      >
        <Iconify icon={LinkTypeMeta[LinkType.LINKEDIN].icon} width={size} height={size} />
      </IconButton>
    </Tooltip>
  );
}
